import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import logo from '../assets/logo.jpg';
import ServicesGrid from '../components/ServicesGrid';
import SEO from '../components/SEO';
import HomeSchema from '../data/seo/HomePageSchema';



const Home = () => {
  const navigate = useNavigate();
  return (
    <div className="font-sans bg-white text-masa-dark">
      {/* Hero Section */}
      <SEO
        title={HomeSchema.pageTitle}
        description={HomeSchema.pageDescription}
        keywords={HomeSchema.pageKeywords}
        canonical={HomeSchema.canonicalUrl}
        schema={HomeSchema.homepageSchemaJson}
        openGraph={{
          title: HomeSchema.pageTitle,
          description: HomeSchema.pageDescription,
          image: logo,
        }}
      />
      <section className="relative overflow-hidden bg-gradient-to-br from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 grid md:grid-cols-2 gap-12 items-center">
          {/* Floating background elements using brand colors */}
          <div className="absolute -top-20 -left-20 w-64 h-64 bg-masa-coral/10 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
          <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-masa-teal/10 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
          <div className="absolute top-1/2 right-1/4 w-64 h-64 bg-masa-yellow/10 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>

          {/* Text content */}
          <div className="relative z-10">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-masa-blue to-masa-teal">
                Compassionate, Personalized
              </span>
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-masa-coral to-masa-magenta">
                Home Care
              </span>
            </h1>

            <div className="text-lg font-medium text-masa-blue mb-4">
              <span className="inline-block px-4 py-2 bg-masa-blue/10 text-masa-dark rounded-full border border-masa-blue/20">
                Compassionate Home Health Services
              </span>
            </div>

            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              Empowering independence and enhancing lives with trusted in-home health services.
              Our qualified caregivers provide respectful, custom support — right where you feel most comfortable.
            </p>

            <div className="flex flex-wrap gap-4">
              <a
                href="tel:+14695730176"
                className="px-8 py-3 bg-gradient-to-r from-masa-coral to-masa-magenta text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all hover:scale-105 transform duration-300"
              >
                Call Now
              </a>
              <Link
                to="/services"
                className="px-8 py-3 border-2 border-masa-blue text-masa-blue font-semibold rounded-lg hover:bg-masa-blue/5 transition-all hover:scale-105 transform duration-300"
              >
                Explore Services
              </Link>
            </div>
          </div>

          {/* Hero Image */}
          <div className="relative z-10">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition duration-500 border-2 border-masa-teal/20">
              <img
                src={logo}
                alt="MASA HILLS Health Services - Caregiver helping elderly person"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-masa-dark mb-4">
              Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-masa-coral to-masa-teal">MASA HILLS HEALTH</span> Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive care solutions tailored to meet your unique needs and enhance your quality of life
            </p>
            {/* Brand accent line */}
            <div className="w-24 h-1 bg-gradient-to-r from-masa-coral via-masa-yellow to-masa-teal mx-auto mt-6 rounded-full"></div>
          </div>
          <ServicesGrid imageHeightClass="h-96" />
        </div>
      </section>

      {/* Commitment Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-masa-blue/5 relative overflow-hidden">
        {/* Floating elements with brand colors */}
        <div className="absolute top-20 left-10 w-32 h-32 rounded-full bg-masa-teal/10 opacity-60 animate-float"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 rounded-full bg-masa-coral/10 opacity-60 animate-float animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/4 w-24 h-24 rounded-full bg-masa-yellow/10 opacity-40 animate-float animation-delay-4000"></div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-masa-blue/10">
            <div className="md:flex">
              {/* Left panel with brand gradient */}
              <div className="md:w-1/2 p-10 bg-gradient-to-br from-masa-blue to-masa-teal text-white flex flex-col justify-center">
                <div className="mb-8">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-6 border-2 border-white/30">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <h3 className="text-3xl font-bold mb-4">Our Commitment to You</h3>
                  <p className="text-white/90 leading-relaxed">
                    At MASA HILLS Health Services, we bring over a decade of dedicated experience serving seniors and families in the Dallas-Fort Worth area.
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-masa-coral rounded-full flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                      <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-white/90">Compassionate, family-like care</p>
                  </div>
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-masa-yellow rounded-full flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                      <svg className="h-3 w-3 text-masa-dark" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-white/90">Highly trained and experienced team</p>
                  </div>
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-masa-magenta rounded-full flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                      <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-white/90">Fully personalized care plans</p>
                  </div>
                </div>
              </div>

              {/* Right panel */}
              <div className="md:w-1/2 p-10 flex flex-col justify-center">
                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-masa-dark mb-4">Experience the Difference</h3>
                  <p className="text-gray-600 mb-6">
                    We treat every client like family, providing care with the same compassion and dedication we extend to our own loved ones.
                  </p>

                  <div className="bg-gradient-to-r from-masa-blue/5 to-masa-teal/5 rounded-xl p-6 mb-6 border border-masa-blue/10">
                    <p className="text-masa-blue font-semibold mb-4">
                      Schedule your complimentary, no-obligation evaluation today
                    </p>
                    <button
                      onClick={() => navigate('/contact')}
                      className="w-full bg-gradient-to-r from-masa-coral to-masa-magenta text-white py-3 rounded-lg font-semibold hover:shadow-md transition-all transform hover:scale-[1.02] hover:from-masa-coral/90 hover:to-masa-magenta/90">
                      Get Started
                    </button>
                  </div>

                  <div className="flex items-center justify-center space-x-6">
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-masa-teal rounded-full mr-2"></div>
                      <span className="text-sm text-gray-600 font-medium">24/7 Availability</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-masa-coral rounded-full mr-2"></div>
                      <span className="text-sm text-gray-600 font-medium">Licensed Professionals</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area with Google Map */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-masa-dark mb-3">
              Serving the <span className="text-transparent bg-clip-text bg-gradient-to-r from-masa-blue to-masa-teal">State of Texas</span> Area
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-4">
              Proudly providing compassionate care throughout the entire State of Texas
            </p>
            {/* Brand accent dots */}
            <div className="flex items-center justify-center space-x-2">
              <div className="w-2 h-2 bg-masa-coral rounded-full"></div>
              <div className="w-2 h-2 bg-masa-yellow rounded-full"></div>
              <div className="w-2 h-2 bg-masa-teal rounded-full"></div>
              <div className="w-2 h-2 bg-masa-blue rounded-full"></div>
              <div className="w-2 h-2 bg-masa-magenta rounded-full"></div>
            </div>
          </div>

          <div className="rounded-2xl overflow-hidden shadow-xl border-2 border-masa-blue/10">
            <iframe
              title="MASA HILLS Health Services - DFW Service Area"
              // src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d107112.1868086356!2d-97.040335!3d32.820929!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x864e6e122f7a4b7d%3A0x9c7b6b7b6b7b6b7b!2sDallas-Fort%20Worth%20Metplex!5e0!3m2!1sen!2sus!4v1680000000000!5m2!1sen!2sus"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d8589697.11743051!2d-106.645646!3d31.968599!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x864c2f647b6c1b7d%3A0x7e3e6e8e8e8e8e8e!2sTexas!5e0!3m2!1sen!2sus!4v1680000000000!5m2!1sen!2sus"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen={false}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full h-[450px] border-0"
            ></iframe>
          </div>

          <div className="mt-8 text-center">
            <p className="text-gray-600 mb-6">
              Not sure if we serve your area? Give us a call to confirm!
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-4 border-2 border-masa-blue text-masa-blue font-semibold rounded-lg shadow-sm hover:bg-masa-blue hover:text-white transition-all duration-300 hover:scale-105 transform"
            >
              <svg className="-ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
              </svg>
              Contact Us Today
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
};

export default Home;